/**
 * Wave 7 / Lot B — l'état de lecture, dit au client en clair.
 *
 * Ce composant est le SEUL endroit du portail qui écrit « chargement »,
 * « hors ligne », « session expirée », « accès refusé » et « erreur ». Il ne rend
 * rien en `ready` : la page reprend alors la main pour distinguer le vide du
 * nominal. C'est ce qui garantit l'ordre imposé sur les 12 routes — le « vide »
 * d'une page n'est atteignable qu'après une lecture réellement aboutie.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Lock, RefreshCw, WifiOff } from "lucide-react";
import type { PortalStatus } from "../../hooks/usePortalResource";

interface Props {
  status: PortalStatus;
  /** Ce que l'écran essaie de lire, au pluriel : « vos factures ». */
  label: string;
  /** Message de l'API, quand elle en donne un (403 « portail coupé »). */
  error?: string | null;
  onRetry?: () => void;
}

function Panel({
  tone,
  icon,
  title,
  children,
  live,
}: {
  tone: "muted" | "warning" | "danger";
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  live: "status" | "alert";
}) {
  const toneClass =
    tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-muted";
  return (
    <div
      role={live}
      className="flex items-start gap-3 rounded-xl2 border border-border bg-card px-4 py-3"
    >
      <span className={`mt-0.5 shrink-0 ${toneClass}`} aria-hidden>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink">{title}</p>
        {children && <div className="mt-1 text-sm text-muted">{children}</div>}
      </div>
    </div>
  );
}

function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      onClick={onRetry}
      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-terracotta hover:bg-cream"
    >
      <RefreshCw size={14} aria-hidden /> Réessayer
    </button>
  );
}

export function PortalState({ status, label, error, onRetry }: Props) {
  if (status === "ready") return null;

  if (status === "loading") {
    return (
      <p role="status" className="text-sm text-muted">
        Chargement de {label}…
      </p>
    );
  }

  if (status === "offline") {
    return (
      <Panel tone="warning" live="status" icon={<WifiOff size={18} />} title="Hors ligne">
        Impossible de joindre votre fournisseur pour l'instant : {label} ne peuvent pas être
        affichées. Rien n'est perdu — reconnectez-vous au réseau puis réessayez.
        {onRetry && <RetryButton onRetry={onRetry} />}
      </Panel>
    );
  }

  if (status === "unauthorized") {
    return (
      <Panel tone="danger" live="alert" icon={<Lock size={18} />} title="Session expirée">
        Votre session a pris fin. Reconnectez-vous pour retrouver {label}.
        <Link
          to="/"
          className="mt-2 inline-flex rounded-lg border border-border px-3 py-1.5 text-sm font-semibold text-terracotta hover:bg-cream"
        >
          Se reconnecter
        </Link>
      </Panel>
    );
  }

  if (status === "forbidden") {
    return (
      <Panel tone="danger" live="alert" icon={<Lock size={18} />} title="Accès refusé">
        {error ?? `Votre fournisseur n'ouvre pas ${label} à votre compte.`}
      </Panel>
    );
  }

  return (
    <Panel
      tone="danger"
      live="alert"
      icon={<AlertTriangle size={18} />}
      title={`Impossible d'afficher ${label}`}
    >
      {error ?? "Le service n'a pas répondu correctement."} Aucune donnée n'est perdue : réessayez
      dans un instant.
      {onRetry && <RetryButton onRetry={onRetry} />}
    </Panel>
  );
}
